import { useMemo } from 'react';
import { accountMatchesRole, accountOptionLabel } from './AccountLookup';
import { toPercent } from '../lib/format';
import type { LocalAccountDto } from '../lib/types';

/**
 * The per-line Account cell on the Purchase Invoice review screen: a lookup over the connected
 * company's Chart of Accounts, with iERP AI's suggestion for the line shown beside it.
 *
 * Two rules the whole feature rests on, both enforced here rather than trusted:
 *
 *   1. A SUGGESTION IS NEVER THE VALUE. `value` is the account the reviewer coded (null = the
 *      company's default expense account, exactly as before). The suggestion is offered as the first
 *      option and as a "Use" action, and becomes the value only when a person picks it. Nothing on
 *      this screen or the server applies it on its own.
 *   2. THE LIST IS THE CHART, NOT A TEXT BOX. A reviewer who cannot name every account id by heart
 *      can still verify or correct a suggestion, because the alternatives are right there. Only when
 *      no chart has been pulled yet does the cell fall back to the old free-text id, so that screen
 *      never gets worse than it was.
 */
export interface LineAccountSuggestion {
  id: string;
  name: string | null;
  /** 0-1 (or 0-100; `toPercent` copes with both). */
  confidence: number | null;
  reason: string | null;
}

export type SuggestionStatus = 'none' | 'suggested' | 'accepted' | 'overridden';

/** How the reviewer's current choice relates to iERP AI's suggestion. */
export function suggestionStatus(
  value: string | null,
  suggestion: LineAccountSuggestion | null,
): SuggestionStatus {
  if (!suggestion) return 'none';
  if (!value) return 'suggested';
  return value === suggestion.id ? 'accepted' : 'overridden';
}

/** The reviewer-facing label for a suggestion state, shared with the Line items panel summary. */
export function describeSuggestion(
  status: SuggestionStatus,
  suggestion: LineAccountSuggestion | null,
): string {
  if (!suggestion || status === 'none') return '';
  const name = suggestion.name ?? suggestion.id;
  const pct = toPercent(suggestion.confidence);
  const confidence = pct === null ? '' : ` (${Math.round(pct)}%)`;
  switch (status) {
    case 'accepted':
      return `Suggestion accepted: ${name}`;
    case 'overridden':
      return `Coded elsewhere; iERP AI suggested ${name}${confidence}`;
    default:
      return `iERP AI suggests ${name}${confidence}`;
  }
}

/** The accounts a purchase line may be coded to, narrowed by the shared filter text. */
export function filterLineAccounts(accounts: LocalAccountDto[], filter: string): LocalAccountDto[] {
  const term = filter.trim().toLowerCase();
  return accounts
    .filter((account) => accountMatchesRole(account, 'expense'))
    .filter((account) => {
      if (!term) return true;
      return [account.accountNumber, account.name, account.fullyQualifiedName, account.accountType]
        .some((field) => (field ?? '').toLowerCase().includes(term));
    });
}

const MAX_OPTIONS = 200;

export interface LineAccountPickerProps {
  lineNo: number;
  /** The reviewer's coded account id; null means the company's default expense account. */
  value: string | null;
  suggestion: LineAccountSuggestion | null;
  /** The connected company's eligible accounts (learningAccounts.allEligible). */
  accounts: LocalAccountDto[];
  accountsLoading: boolean;
  /** Filter text shared by every line's picker, so one search narrows the whole table. */
  filter: string;
  readOnly: boolean;
  onChange: (accountId: string | null) => void;
}

export function LineAccountPicker({
  lineNo,
  value,
  suggestion,
  accounts,
  accountsLoading,
  filter,
  readOnly,
  onChange,
}: LineAccountPickerProps): JSX.Element {
  const status = suggestionStatus(value, suggestion);
  const summary = describeSuggestion(status, suggestion);

  const byId = useMemo(
    () => new Map(accounts.map((account) => [account.externalAccountId, account])),
    [accounts],
  );
  const matching = useMemo(() => filterLineAccounts(accounts, filter), [accounts, filter]);

  const suggestionNote = suggestion ? (
    <span className="cell-sub line-account-suggestion" title={suggestion.reason ?? undefined}>
      {summary}
      {suggestion.reason ? <span className="visually-hidden"> Reason: {suggestion.reason}</span> : null}
      {status === 'suggested' && !readOnly ? (
        <>
          {' '}
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            aria-label={`Use the suggested account for line ${lineNo}`}
            onClick={() => onChange(suggestion.id)}
          >
            Use
          </button>
        </>
      ) : null}
    </span>
  ) : null;

  // No chart to choose from yet: keep the old free-text id so the screen never regresses, and still
  // show what iERP AI suggested so the reviewer can type it in deliberately.
  if (!accountsLoading && accounts.length === 0) {
    return (
      <div className="line-account">
        <input
          className="input input-inline"
          value={value ?? ''}
          readOnly={readOnly}
          placeholder="Default"
          aria-label={`Line ${lineNo} QuickBooks account id`}
          onChange={(event) => onChange(event.target.value || null)}
        />
        {suggestionNote}
      </div>
    );
  }

  const visible = matching.slice(0, MAX_OPTIONS).filter((account) => account.externalAccountId !== suggestion?.id);
  const suggestedAccount = suggestion ? byId.get(suggestion.id) : undefined;
  const suggestedLabel = suggestion
    ? `${suggestedAccount ? accountOptionLabel(suggestedAccount) : suggestion.name ?? suggestion.id} — suggested`
    : null;

  // A saved coding that is no longer in the filtered list (or no longer in the chart) stays
  // selectable, so opening the screen never silently loses a choice somebody already made.
  const currentAccount = value ? byId.get(value) : undefined;
  const keepCurrent =
    value !== null
    && value !== suggestion?.id
    && !visible.some((account) => account.externalAccountId === value);

  const chartLabel =
    matching.length > MAX_OPTIONS
      ? `Chart of Accounts (first ${MAX_OPTIONS} of ${matching.length}; filter to narrow)`
      : 'Chart of Accounts';

  return (
    <div className="line-account">
      <select
        className="input input-inline select"
        value={value ?? ''}
        disabled={readOnly || accountsLoading}
        aria-label={`Line ${lineNo} QuickBooks account`}
        onChange={(event) => onChange(event.target.value || null)}
      >
        <option value="">{accountsLoading ? 'Loading accounts…' : 'Default expense account'}</option>
        {suggestion && suggestedLabel ? (
          <optgroup label="Suggested by iERP AI">
            <option value={suggestion.id}>{suggestedLabel}</option>
          </optgroup>
        ) : null}
        {keepCurrent ? (
          <option value={value!}>
            {currentAccount ? accountOptionLabel(currentAccount) : value} — current selection
          </option>
        ) : null}
        <optgroup label={chartLabel}>
          {visible.map((account) => (
            <option key={account.externalAccountId} value={account.externalAccountId}>
              {accountOptionLabel(account)}
            </option>
          ))}
        </optgroup>
      </select>
      {suggestionNote}
    </div>
  );
}
